import { isNil, isString } from 'lodash';
import { TouchableOpacity } from 'react-native';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBMessageReactionsAndSeenBy } from '@shared/scenes/chat/thread/containers/messageReactionsAndSeenBy';
import { MBChannelTypeUtil } from '@shared/scenes/chat/thread/util/channelType';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import * as MBSystemMessageTemplates from './systemMessageTemplates';

export interface MBSystemMessageProps {
  message: API.ChatMessageFragment;
  layout: MBChatMessageLayoutOptions;
  channelSlug: string;
  hovering: boolean;
  isDesktop: boolean;
}

export interface MBSystemMessageWithCallbacksProps extends Omit<MBSystemMessageProps, 'hovering'> {
  isDM: boolean;
  isDesktop: boolean;
  onOpenCard(cardId: string): void;
}

type SystemMessageComponent =
  | typeof MBSystemMessageTemplates.ChannelCreated
  | typeof MBSystemMessageTemplates.MessageRemoved
  | typeof MBSystemMessageTemplates.UserJoined
  | typeof MBSystemMessageTemplates.UsersAdded
  | typeof MBSystemMessageTemplates.UsersRemoved
  | typeof MBSystemMessageTemplates.MeRemoved
  | typeof MBSystemMessageTemplates.UserNotified
  | typeof MBSystemMessageTemplates.UserNotified
  | typeof MBSystemMessageTemplates.Card
  | null;

/*
    Render a system message depending on its subtype. Every system message
    template will figure out what content to show depending on the system
    message and its meta.
*/
export function MBSystemMessage({
  message,
  layout,
  channelSlug,
  onOpenCard,
  isDM,
  isDesktop,
}: MBSystemMessageWithCallbacksProps) {
  let Component: SystemMessageComponent = null;
  let onPress: VoidFunction | undefined = undefined;
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(!isDesktop); // treat hovered as true on mobile

  // Note(fant): I opted for ifs instead of a huge switch block
  // since it's less wordy
  if (message.subType === 'CHANNEL_CREATED') {
    Component = MBSystemMessageTemplates.ChannelCreated;
  }

  if (
    message.subType === 'MESSAGE_DELETED' ||
    message.subType === 'COMMENT_DELETED' ||
    (message as any as API.ThreadMessageFragment).subType === 'THREAD_MESSAGE_DELETED'
  ) {
    Component = MBSystemMessageTemplates.MessageRemoved;
  }

  if (message.subType === 'USER_JOINED') {
    Component = MBSystemMessageTemplates.UserJoined;
  }

  if (message.subType === 'USERS_ADDED') {
    Component = MBSystemMessageTemplates.UsersAdded;
  }

  if (message.subType === 'USER_REMOVED_FROM_CHANNEL') {
    Component = MBSystemMessageTemplates.UsersRemoved;
  }

  if (message.subType === 'ME_REMOVED_FROM_CHANNEL') {
    Component = MBSystemMessageTemplates.MeRemoved;
  }

  if (message.subType === 'USER_NOTIFIED') {
    Component = MBSystemMessageTemplates.UserNotified;
  }

  // TODO(fant): should this show the same content as USER_NOTIFIED?
  if (message.subType === 'USERS_TEXTED') {
    Component = MBSystemMessageTemplates.UserNotified;
  }

  if (
    isString(message.subType) &&
    ['CARD_CREATED', 'CARD_UPDATED', 'CARD_DELETED', 'CARD_REMINDER_SENT'].includes(message.subType)
  ) {
    Component = MBSystemMessageTemplates.Card;
  }

  // If we have not set a component, message subtype hasn't
  // been found among the types above. We therefore can't
  // render it. Here we ideally tell the backend what the
  // frontend can render so that the backend can return
  // fallback messages if the frontend doesn't have specific
  // implementation. Just rendering message.content should
  // be a reasonable fallback.
  if (!Component) {
    return null;
  }

  const cardId = message.meta.cardId;
  if (
    (message as any as API.ThreadMessageFragment).subType === 'CARD_CREATED' &&
    !isNil(cardId) &&
    !MBChannelTypeUtil.isThread(channelSlug)
  ) {
    onPress = () => {
      onOpenCard(cardId);
    };
  }

  return (
    <View fullWidth paddingVertical={1} paddingLeft={2.25}>
      <MBHover {...{ onHoverStart, onHoverEnd }}>
        <TouchableOpacity onPress={onPress} disabled={!onPress}>
          <Component
            message={message}
            layout={layout}
            isDesktop={isDesktop}
            channelSlug={channelSlug}
            hovering={isHovered}
          />
        </TouchableOpacity>
      </MBHover>

      <View paddingLeft={3.5}>
        <MBMessageReactionsAndSeenBy
          message={message}
          channelSlug={channelSlug}
          align="left"
          layout={layout}
          isDM={isDM}
          marginTop={0}
        />
      </View>
    </View>
  );
}
